/* eslint-disable no-console, fp/no-loops, no-case-declarations */
import { cloneDeep, isEmpty } from 'lodash';
import type { Node } from 'ricos-schema';
import { Decoration_Type, Node_Type, RichContent } from 'ricos-schema';

import { removeInlineHeaderRanges } from '../../../normalization/removeInlineHeaderRanges';
import type { DraftContent, RicosContentBlock } from '../../../types';
import { getValidHexColor } from '../../../utils';
import { generateId } from '../../generateRandomId';
import { createParagraphNode, initializeMetadata } from '../../nodeUtils';
import { BlockType, FROM_DRAFT_LIST_TYPE, HeaderLevel } from '../consts';
import { getEntity, getNodeStyle, getTextStyle } from './getRicosEntityData';
import { getTextNodes } from './getTextNodes';
import { parseDocStyle } from './parse-doc-style';

export interface FromDraftOptions {
  ignoreUnsupportedValues?: boolean;
}

export const ensureRicosContent = (
  content: RichContent | DraftContent,
  opts?: FromDraftOptions
): RichContent => ('blocks' in content ? fromDraft(content, opts) : content);

interface Cell {
  style?: { backgroundColor?: string; verticalAlign?: string };
  border?: { top?: string; left?: string; right?: string; bottom?: string };
  content: DraftContent;
}

type Row = Record<string, Cell>;

const parseCollapsible = (entity, opts): Node | undefined =>
  entity.pairs.map((pair: { key: string; title: DraftContent; content: DraftContent }) => ({
    id: pair.key,
    type: Node_Type.COLLAPSIBLE_ITEM,
    nodes: [
      {
        id: generateId(),
        type: Node_Type.COLLAPSIBLE_ITEM_TITLE,
        nodes: fromDraft(pair.title, opts).nodes,
      },
      {
        id: generateId(),
        type: Node_Type.COLLAPSIBLE_ITEM_BODY,
        nodes: fromDraft(pair.content, opts).nodes,
      },
    ],
  }));

const parseTable = (entity, opts) =>
  Object.entries(entity.rows).map(([, row]: [string, Row]) => ({
    id: generateId(),
    type: Node_Type.TABLE_ROW,
    nodes: Object.entries(row.columns ?? {}).map(([, cell]: [string, Cell]) => ({
      id: generateId(),
      type: Node_Type.TABLE_CELL,
      tableCellData: {
        cellStyle: {
          verticalAlignment: cell.style?.verticalAlign?.toUpperCase(),
          backgroundColor: getValidHexColor(cell.style?.backgroundColor)?.toUpperCase(),
        },
        borderColors: {
          top: getValidHexColor(cell.border?.top)?.toUpperCase(),
          left: getValidHexColor(cell.border?.left)?.toUpperCase(),
          right: getValidHexColor(cell.border?.right)?.toUpperCase(),
          bottom: getValidHexColor(cell.border?.bottom)?.toUpperCase(),
        },
      },
      nodes: fromDraft(cell.content, opts).nodes,
    })),
  }));

const nestedNodesConverters = {
  [Node_Type.COLLAPSIBLE_LIST]: parseCollapsible,
  [Node_Type.TABLE]: parseTable,
};

const normalizeBlock = block => {
  if (block.type === 'atomic' && block.text.startsWith(' ') && block.text?.length > 1) {
    // in atomic block, default text is ' ', hence length > 1
    // this seems to be text block, so treat one as such
    block.type = 'unstyled';
  }

  block.depth = block.depth || 0;
  block.entityRanges = block.entityRanges || [];
  block.inlineStyleRanges = (block.inlineStyleRanges || [])
    .filter(range => range.offset >= 0)
    .map(range => {
      // Mapping old foreground color decoration to new one
      if (range.style.startsWith('#')) {
        return {
          ...range,
          style: `{"FG":"${range.style}"}`,
        };
      }

      // Removing floats in font-size styles
      try {
        const parsedStyle = JSON.parse(range.style);
        if ('font-size' in parsedStyle) {
          const [value, unit] = parsedStyle['font-size'].split(/(px|em)/gi);
          return {
            ...range,
            style: `{"font-size":"${parseInt(value, 10)}${unit}"}`,
          };
        }
      } catch {}

      return range;
    });
};

export const fromDraft = (draftJSON: DraftContent, opts: FromDraftOptions = {}): RichContent => {
  const { blocks = [], entityMap = {}, documentStyle, ID: id } = cloneDeep(draftJSON);
  const nodes: Node[] = [];
  const contentIdPrefix = Math.random().toString(36).substr(2, 9);
  const usedKeys: Record<string, boolean> = {};
  const withDepth = (block: RicosContentBlock, depth = 0) => ({ ...block, depth });

  const ensureUniqueKey = (key: string) => {
    if (usedKeys[key]) {
      console.warn(`WARN! Duplicate block key "${key}"!`);
      // eslint-disable-next-line no-param-reassign
      key = generateId();
    }
    usedKeys[key] = true;
    return key;
  };

  const parseBlock = index => {
    let block = blocks[index];
    if (!block) {
      return -1;
    }

    // this step is outside of normalizeBlock as the function is not
    // mutating and even though is perfectly good but can not be used
    // in the function which is expected to mutate data
    block = removeInlineHeaderRanges(block);
    normalizeBlock(block);

    switch (block.type) {
      case BlockType.Atomic:
        const atomicBlocks = parseAtomicBlock(block);
        atomicBlocks?.forEach(atomicBlock => {
          nodes.push(atomicBlock);
        });
        return index + 1;
      case BlockType.Blockquote:
        nodes.push(parseQuoteBlock(block));
        return index + 1;
      case BlockType.CodeBlock:
        nodes.push(parseCodeBlock(block));
        return index + 1;
      case BlockType.HeaderOne:
      case BlockType.HeaderTwo:
      case BlockType.HeaderThree:
      case BlockType.HeaderFour:
      case BlockType.HeaderFive:
      case BlockType.HeaderSix:
        nodes.push(parseHeadingBlock(block));
        return index + 1;
      case BlockType.OrderedListItem:
      case BlockType.UnorderedListItem:
        const { node, nextIndex } = parseListBlocks(index);
        nodes.push(node);
        return nextIndex;
      case undefined:
      case BlockType.Unstyled:
        nodes.push(parseTextBlock(block));
        return index + 1;
      default:
        if (opts.ignoreUnsupportedValues) {
          return index + 1;
        } else {
          throw Error(`ERROR! Unknown block type "${block.type}"!`);
        }
    }
  };

  const parseAtomicBlock = (block: RicosContentBlock): Node[] | null => {
    const { key, data, entityRanges } = block;
    if (entityRanges && entityRanges.length) {
      return entityRanges
        .map(entityRange => {
          const entity = getEntity(entityRange.key, entityMap);

          if (entity && entity.type) {
            switch (entity.type) {
              case Decoration_Type.LINK:
              case Decoration_Type.ANCHOR:
              case Decoration_Type.MENTION: {
                return parseTextBlock(block);
              }
              default: {
                const nodes = nestedNodesConverters[entity.type]?.(entity, opts) || [];

                if (
                  !nodes.length &&
                  (entity.type === Node_Type.TABLE || entity.type === Node_Type.COLLAPSIBLE_LIST)
                ) {
                  // Table/collapsible list are invalid without child nodes
                  return null;
                }

                const shouldAddStyle = ![
                  Node_Type.IMAGE,
                  Node_Type.VIDEO,
                  Node_Type.DIVIDER,
                  Node_Type.HTML,
                  Node_Type.GALLERY,
                  Node_Type.BUTTON,
                  Node_Type.FILE,
                  Node_Type.COLLAPSIBLE_LIST,
                  Node_Type.GIF,
                  Node_Type.TABLE,
                  Node_Type.APP_EMBED,
                  Node_Type.POLL,
                ].includes(entity.type);

                return {
                  id: ensureUniqueKey(key),
                  nodes,
                  ...(shouldAddStyle ? { style: getNodeStyle(data) } : {}),
                  ...entity,
                };
              }
            }
          }

          return null;
        })
        .filter(block => !!block);
    }

    return null;
  };

  const parseQuoteBlock = (block: RicosContentBlock): Node => ({
    id: ensureUniqueKey(block.key),
    type: Node_Type.BLOCKQUOTE,
    nodes: [parseTextBlock(withDepth(block))],
    style: getNodeStyle(block.data),
    blockquoteData: { indentation: block.depth },
  });

  const parseCodeBlock = (block: RicosContentBlock): Node => ({
    id: ensureUniqueKey(block.key),
    type: Node_Type.CODE_BLOCK,
    nodes: getTextNodes(block, entityMap, opts, false),
    style: getNodeStyle(block.data),
    codeBlockData: {
      textStyle: getTextStyle(block.data),
    },
  });

  const parseHeadingBlock = (block: RicosContentBlock): Node => {
    const getLevel = (blockType: string) => {
      if (Object.keys(HeaderLevel).includes(blockType)) {
        return HeaderLevel[blockType];
      }
      throw Error(`ERROR! Unknown header level "${blockType}"!`);
    };
    return {
      id: ensureUniqueKey(block.key),
      type: Node_Type.HEADING,
      headingData: {
        level: getLevel(block.type),
        indentation: block.depth,
        textStyle: getTextStyle(block.data),
      },
      nodes: getTextNodes(block, entityMap, opts),
      style: getNodeStyle(block.data),
    };
  };

  const parseTextBlock = (block: RicosContentBlock): Node => {
    const paragraphNode: Node = createParagraphNode(
      [],
      { textStyle: getTextStyle(block.data) },
      getNodeStyle(block.data)
    );

    switch (block.type) {
      case BlockType.Unstyled:
        paragraphNode.id = ensureUniqueKey(block.key);
      // falls through
      case undefined:
      case BlockType.Atomic:
      case BlockType.Blockquote:
        if (paragraphNode.paragraphData) {
          paragraphNode.paragraphData.indentation = parseDepth(block.depth);
        }
        break;
      case BlockType.OrderedListItem:
      case BlockType.UnorderedListItem:
        if (paragraphNode.paragraphData) {
          paragraphNode.paragraphData.indentation = parseDepth(block.depth);
        }
        paragraphNode.id = ensureUniqueKey(block.key);
        break;
      default:
    }

    const nodes = getTextNodes(block, entityMap, opts);

    if (!isEmpty(nodes)) {
      paragraphNode.nodes = nodes;
    }

    return paragraphNode;
  };

  const createListItem = (block: RicosContentBlock): Node => ({
    id: generateId(contentIdPrefix),
    type: Node_Type.LIST_ITEM,
    nodes: [parseTextBlock(withDepth(block))],
  });

  const isListBlock = (block: RicosContentBlock): boolean =>
    FROM_DRAFT_LIST_TYPE[block.type] !== undefined;

  const parseListBlocks = (listStartIndex: number): { node: Node; nextIndex: number } => {
    const { type: listType, depth } = blocks[listStartIndex];
    const listNodes: Node[] = [];
    let searchIndex = listStartIndex;
    let nextBlock = blocks[searchIndex];

    while (nextBlock && isListBlock(nextBlock) && nextBlock.depth >= depth) {
      normalizeBlock(nextBlock);

      if (nextBlock.depth > depth) {
        const { node, nextIndex } = parseListBlocks(searchIndex);
        const prevNode = listNodes[listNodes.length - 1];
        if (prevNode.nodes) {
          prevNode.nodes.push(node);
        } else {
          prevNode.nodes = [node];
        }
        searchIndex = nextIndex;
      } else if (nextBlock.type !== listType) {
        // If list type changes and we are on the same depth we consider this as a separate list
        break;
      } else {
        listNodes.push(createListItem(nextBlock));
        searchIndex++;
      }
      nextBlock = blocks[searchIndex];
    }

    const listData =
      FROM_DRAFT_LIST_TYPE[listType] === Node_Type.ORDERED_LIST
        ? 'orderedListData'
        : 'bulletedListData';

    return {
      node: {
        id: generateId(contentIdPrefix),
        type: FROM_DRAFT_LIST_TYPE[listType],
        nodes: listNodes,
        [listData]: { indentation: depth },
      },
      nextIndex: searchIndex,
    };
  };

  let i = 0;
  while (i !== -1) {
    i = parseBlock(i);
  }

  const content: RichContent = {
    nodes,
    metadata: initializeMetadata({ id }),
    documentStyle: documentStyle && parseDocStyle(documentStyle),
  };

  return RichContent.fromJSON(content);
};

const parseDepth = (depth: string | number): number => {
  if (isNaN(parseInt(depth.toString(), 10))) {
    return 0;
  }

  return parseInt(depth.toString(), 10);
};
